import type { ReactNode } from "react";
import "./ui.css";

interface StatNumberProps {
	label: string;
	value: ReactNode;
	sub?: ReactNode;
	isAlert?: boolean;
	/* EQU-803. Amber, for a count that wants attention without claiming something
	   is broken ("never reviewed"). Mirrors `isAlert` rather than replacing it
	   with a tone union, so no existing call site changes. `isAlert` wins when
	   both are set — red outranks amber. */
	isWarning?: boolean;
	/* "md" (default) is the canonical KPI number: 26px/800. "sm" (24px/700) is for
	   dense strips where a full-size number crowds the row — the business-health
	   band is the reason this exists. Before this prop the dashboard could not
	   express what it needed, so it hand-rolled 28/700 and 24/600 instead of
	   adopting the primitive. Adding a size is the fix; a third scale is not. */
	size?: "md" | "sm";
}

/* Did NOT reuse ColorNumber for the value: ColorNumber colors text red/green
   by sign, but .firm-stat-value is ink-colored (or red only when isAlert),
   so ColorNumber would wrongly tint every positive stat green. The value is
   rendered as plain ink text; callers can pass a formatted string/node. */
/* <div>, not <p>, for value and sub. Both are typed ReactNode, so a caller can
   legitimately pass a component that renders block elements — an AntD Progress
   inside `sub` did exactly that and produced
   "validateDOMNesting: <div> cannot appear as a descendant of <p>", which the
   browser resolves by silently auto-closing the paragraph and mangling the
   layout. The .ui-stat-* classes set their own margins, so a div renders
   identically to the p it replaces. */
export function StatNumber({
	label,
	value,
	sub,
	isAlert,
	isWarning,
	size = "md",
}: StatNumberProps) {
	const tone = isAlert ? " is-alert" : isWarning ? " is-warning" : "";
	return (
		<div>
			<div className="ui-stat-label">{label}</div>
			<div className={`ui-stat-value${size === "sm" ? " is-sm" : ""}${tone}`}>
				{value}
			</div>
			{sub != null && <div className="ui-stat-sub">{sub}</div>}
		</div>
	);
}
